import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSynchronizeCarts } from '@hooks/businessContainer';
import { useShoppingCart } from '@hooks/cart/useShoppingCart';
import CheckoutProductErrorsModal from '@components/ProceedCheckout/CheckoutProductErrorsModal';
import { CHECKOUT_ERRORS } from '@monofront/business/constants';
import {
  CheckoutAppEnum,
  CheckoutOriginPlatforms,
  ICheckoutProductError,
} from '@monofront/types';
import logger from '@utils/logger';
import { isMobileApp } from '@utils/environment';
import { ApolloError } from '@apollo/client';

const checkoutProductError = ({ error }) =>
  error === CHECKOUT_ERRORS.STOCK_IS_INSUFFICIENT ||
  error === CHECKOUT_ERRORS.PRODUCT_VARIANT_NOT_EXIST ||
  error === CHECKOUT_ERRORS.PRODUCT_NOT_EXIST;

/**
 * Owns the out-of-stock modal: opens it when the checkout machine sends the
 * customer back with `isCheckoutProductErrorModalOpen`, then removes the
 * faulty products and resumes the checkout.
 */
const CheckoutProductErrorsModalHandler = () => {
  const router = useRouter();
  const {
    shoppingCart,
    shoppingCartId,
    checkoutProductErrors,
    setCheckoutProductErrors,
    deleteProductsFromShoppingCart,
  } = useShoppingCart();
  const { forceSynchronizeCarts } = useSynchronizeCarts();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(
    Boolean(router?.query?.isCheckoutProductErrorModalOpen),
  );

  const tErrors = useTranslations('Errors');

  const handleErrors = (errors: ICheckoutProductError[]) => {
    if (errors) {
      if (errors?.some(checkoutProductError)) {
        setCheckoutProductErrors(errors.filter(checkoutProductError));
        setIsModalOpen(true);
      } else {
        toast(tErrors('redirectionError'), {
          progressClassName: 'toast-error',
        });
      }
    }
  };

  const handleError = (error: ApolloError) => {
    logger.error(error);

    toast(tErrors('redirectionError'), {
      progressClassName: 'toast-error',
    });
  };

  return (
    <CheckoutProductErrorsModal
      isOpen={isModalOpen}
      onValidate={async () => {
        setIsModalOpen(false);
        await deleteProductsFromShoppingCart(
          shoppingCartId,
          checkoutProductErrors.map((error) => ({
            sku: error.sku,
          })),
        );
        forceSynchronizeCarts(
          shoppingCart.products,
          {
            checkoutOrigin: isMobileApp
              ? CheckoutAppEnum.MOBILE
              : CheckoutAppEnum.WEB,
            checkoutOriginPlatform: isMobileApp
              ? window._lfMobilePlatform ?? CheckoutOriginPlatforms.WEB
              : CheckoutOriginPlatforms.WEB,
          },
          handleError,
        ).then(async ({ cart, errors }) => {
          if (cart) await router.push('/checkout/delivery');
          else if (errors) handleErrors(errors);
        });
      }}
      onClose={() => setIsModalOpen(false)}
      checkoutProductErrors={checkoutProductErrors}
      shoppingCart={shoppingCart}
    />
  );
};

export default CheckoutProductErrorsModalHandler;
